import { lazy, Suspense, useEffect } from 'react';
import { usePostToken } from './hooks/use_post_token';
import { useRequestToken } from './hooks/use_request_token';
import { usePreferredLanguage } from './hooks/use_language_preference';
import { webViewDispatcher } from '../inter_op/native_dispatchers/web_view_dispatcher/web_view_dispatcher';
import { WebViewDispatcherAction } from '../inter_op/native_dispatchers/web_view_dispatcher/web_view_dispatcher.constants';
import { useInitialise } from './hooks/use_initialise';
import { Spinner } from '../components/spinner/spinner';
import { useDeepLinkPost } from './hooks/use_deep_link_post';
import { ApolloProvider } from '../gql/apollo_provider';
import { useResetBadgeCount } from './hooks/use_reset_badge_count';
import { useAppState } from '../contexts/app_state/use_app_state_context';
import { AppState } from '../contexts/app_state/app_state_context';

const FeedReader = lazy(() => import('../components/feed_reader/feed_reader'));

const BadgeResetter = () => {
  useResetBadgeCount();
  return null;
};

const useAppHooks = () => {
  const { apiEndpoint } = useInitialise();
  const { deepLinkUri } = useDeepLinkPost();

  usePostToken();
  usePreferredLanguage();
  useRequestToken();
  return { apiEndpoint, deepLinkUri };
};

function dispatchInitialised() {
  webViewDispatcher.postMessage({ action: WebViewDispatcherAction.initialised });
}

const INITIALISE_TIMEOUT = 3000;
const INVALID_TIMER_ID = 0;

function App() {
  const { apiEndpoint, deepLinkUri } = useAppHooks();
  const { status: appStatus, isInitialised } = useAppState();

  useEffect(() => {
    let intervalId = INVALID_TIMER_ID;
    if (appStatus === AppState.active && !isInitialised) {
      dispatchInitialised();
      intervalId = window.setInterval(dispatchInitialised, INITIALISE_TIMEOUT);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [appStatus, isInitialised]);

  if (apiEndpoint === null) {
    return <Spinner />;
  }

  return (
    <ApolloProvider apiEndpoint={apiEndpoint}>
      <BadgeResetter />
      <Suspense fallback={<Spinner />}>
        <FeedReader apiEndpoint={apiEndpoint} deepLinkUri={deepLinkUri} />
      </Suspense>
    </ApolloProvider>
  );
}

export default App;
